export const SORT_OPTIONS = {
  byOptions: [
    { label: "Alphabetical", value: "rfp_name" },
    { label: "Date Created", value: "created_at" },
    { label: "Recently Updated", value: "last_updated_at" },
    { label: "Project Stage", value: "current_stage" },
  ],
  orderOptions: [
    { label: "Ascending", value: 1 },
    { label: "Descending", value: -1 },
  ],
};

export const COMPLETED_SORT_OPTIONS = {
  byOptions: [
    { label: "Alphabetical", value: "rfp_name" },
    { label: "Date Created", value: "created_at" },
    { label: "Completed Date", value: "last_updated_at" },
    { label: "Project Stage", value: "current_stage" },
  ],
  orderOptions: [
    { label: "Ascending", value: 1 },
    { label: "Descending", value: -1 },
  ],
};

export const DEFAULT_VISIBLE_COLUMNS = [
  "rfp_name",
  "rfp_id",
  "created_at",
  "progress",
  "next_stage",
];

export const ALL_COLUMNS = [
  { key: "rfp_name", label: "RFP Name", fixed: true },
  { key: "rfp_id", label: "RFP ID", fixed: true },
  { key: "created_at", label: "Date Created", fixed: true },
  { key: "updated_at", label: "Last Updated" },
  { key: "progress", label: "Progress", fixed: true },
  { key: "next_stage", label: "Next Action Needed", fixed: true },
  { key: "rfp_response", label: "RFP Award Date" },
  { key: "stakeholders", label: "Stakeholders" },
  { key: "vendors", label: "Vendor" },
  { key: "budget", label: "Budget" },
];

export const PHASE_ACTIONS: Record<string, string> = {
  "UPLOAD RFP": "Edit",
  "INVITE STAKEHOLDERS": "Manage",
  "SET CRITICAL REQUIREMENTS": "Edit",
  "ASSIGN WEIGHTS": "Adjust",
  "ENTER BUDGET": "Update",
  "UPLOAD RPF RESPONSE": "Manage",
  "IDENTIFY EXISTING SUPPLIERS": "Edit",
  "SET EVALUTION CRITERIA": "Modify",
};

export const GENERATE_PHASE_ACTIONS: Record<string, string> = {
  "Define Project": "Edit",
  "INVITE STAKEHOLDERS": "Manage",
  "Build Template": "Edit",
  "Refine Requirements": "Manage",
  "Download Tailored RFP": "Adjust",
};

export const HR_PHASE_ACTIONS: Record<string, string> = {
  "Set Evaluation Criteria": "Adjust",
  "INVITE STAKEHOLDERS": "Manage",
  "Upload Job Posting": "Edit",
  "Upload the Applications": "Manage",
};

export const HR_GENERATE_PHASE_ACTIONS_STEPS = [
  "upload-applications",
  "invite",
  "requirement",
  "resume-upload",
] as const;

export type HR_GENERATE_PHASE_ACTIONS_STEP = (typeof HR_GENERATE_PHASE_ACTIONS_STEPS)[number];

export const HR_GENERATE_PHASE_ACTIONS_STEP_MAPPING: HR_GENERATE_PHASE_ACTIONS_STEP[][] = [
  ["upload-applications"],
  ["invite"],
  ["requirement"],
  ["resume-upload"],
];

// Mapping of phase names to their required permissions
export const PHASE_PERMISSIONS: Record<string, keyof ReturnType<typeof import('../../../hooks/useAccessControl').useAccessControl>> = {
  "UPLOAD RFP": "canUploadRfp",
  "INVITE STAKEHOLDERS": "canInviteStakeholders",
  "SET CRITICAL REQUIREMENTS": "canSetCriticalRequirements",
  "ASSIGN WEIGHTS": "canAssignWeights",
  "ENTER BUDGET": "canDefineBudget",
  "UPLOAD RPF RESPONSE": "canUploadRfpResponsses",
  "IDENTIFY EXISTING SUPPLIERS": "canIdentifyExistingSupplier",
  "SET EVALUTION CRITERIA": "canSetEvaluationCriteria",
};

export const GENERATE_PHASE_PERMISSIONS: Record<string, keyof ReturnType<typeof import('../../../hooks/useAccessControl').useAccessControl>> = {
  "Define Project": "canDefineProject",
  "INVITE STAKEHOLDERS": "canInviteStakeholders",
  "Build Template": "canGenerateRfp",
  "Refine Requirements": "canAccessRefinedRequirements",
  "Download Tailored RFP": "canGenerateRfp",
};

// Helper function to check if a phase action should be enabled
export const getPhaseActionButtonType = (
  phaseName: string,
  rfpType: "create" | "analyze",
  permissions: ReturnType<typeof import('../../../hooks/useAccessControl').useAccessControl>,
  userProfile?: { hasAccess: boolean },
  buttonType?: "Primary" | "Default" | "Danger" | "Text",
): "Text" | "Disabled" | "Primary" | "Default" | "Danger" => {
  // First check general access
  if (!userProfile?.hasAccess) return "Disabled";
  
  // If user is admin, allow everything
  if (permissions.isAdmin) return buttonType || "Text";
  
  // Get the required permission for this phase
  const permissionMap = rfpType === "analyze" ? PHASE_PERMISSIONS : GENERATE_PHASE_PERMISSIONS;
  const requiredPermission = permissionMap[phaseName];
  
  // If no specific permission is defined, allow if user has general access
  if (!requiredPermission) return buttonType || "Text";
  
  // Check if user has the specific permission
  return permissions[requiredPermission] ? buttonType || "Text" : "Disabled";
};

export const TABLEOFCONTENTS = [
  "Overview",
  "Executive Summary",
  "Pricing",
  "Comparative Advantages",
  "Risk",
  "Vendor History",
  "Vendor Scorecard",
  "Appendix",
];

export const TEAM_MEMBERS = [
  {
    role: "Key Decision Maker",
    name: "Tim Middleton",
    avatar: "https://randomuser.me/api/portraits/men/1.jpg",
  },
  {
    role: "Finance Stakeholders",
    name: "Frank James",
    initials: "FJ",
    color: "bg-pink-200 text-pink-700",
  },
  {
    role: "Tech Stakeholders",
    name: "Beth Lee",
    avatar: "https://randomuser.me/api/portraits/women/2.jpg",
  },
  {
    role: "Business Stakeholders",
    name: "Chris Sullivan",
    avatar: "https://randomuser.me/api/portraits/men/3.jpg",
  },
  {
    role: "Legal Stakeholders",
    name: "Jess Gonzalez",
    initials: "JG",
    color: "bg-yellow-200 text-yellow-700",
  },
];

export const INTAKE_FLOW_SCRATCH = "scratchIntake";
export const ANALYZE_JOB_APPLICATIONS = "analyzeJobApplications";
export const INTAKE_FLOW_RFP_DEFINED = "intake flow3";
export const COOKIE_RFP_ID_KEY = "rfp_id";

type Plan = {
  name: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  billedAs?: string;
  planId?: string;
  cta: string;
  features: (string | boolean)[];
  footer: string;
  bgColorCode: string;
};

export const featureLabels = [
  "Projects per Month (Create/ Analyze)",
  "Creator Seats",
  "Viewers Seats",
  "Intelligent RFP Authoring",
  "Intelligent Response Analysis",
  "RFP Refinement Engine",
  "Advanced Response Analysis Features",
  "Team collaboration suite",
  "Negotiation Module",
  "Automated Supplier Coordination",
  "Custom ERP/VMS Integrations",
  "Admin Access & Controls",
  "Single Sign On (SSO)",
  "Support",
  "Advanced Security Audits & Controls",
  "Performace and Uptime SLAs",
  "CSM & Strategic Reviews"
];

export const plans: Plan[] = [
  {
    name: "Starter",
    description: "For individuals to use core AI modules",
    monthlyPrice: "Free",
    annualPrice: "Free",
    cta: "Get Started",
    bgColorCode: "#FFFFFF",
    features: [
      "2",
      "1",
      "Up to 5",
      true,
      true,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      "Standard",
      false,
      false,
      false,
    ],
    footer: "Standard",
  },
  {
    name: "Professional",
    description: "For the dedicated sourcing power user",
    monthlyPrice: "$999 / month",
    annualPrice: "$833 / month",
    billedAs: "Billed as $9,990 annually",
    planId: "price_1RybWQErXdrKI3tNy1TloGlU",
    cta: "Start your Pro trial",
    bgColorCode: "#F0F5FB",
    features: [
      "5",
      "1",
      "Unlimited",
      true,
      true,
      true,
      true,
      false,
      false,
      false,
      false,
      false,
      false,
      "Standard",
      false,
      false,
      false,
    ],
    footer: "Standard",
  },
  {
    name: "Teams",
    description:
      "For teams needing the full suite with collaboration and security",
    monthlyPrice: "$2,499 / month",
    annualPrice: "$2,083 / month",
    billedAs: "Billed as $24,990 annually",
    planId: "price_1RybXlErXdrKI3tN2p2D8UR2",
    bgColorCode: "#E4ECF7",
    cta: "Request Demo",
    features: [
      "15",
      "Up to 3",
      "Unlimited",
      true,
      true,
      true,
      true,
      true,
      false,
      false,
      false,
      true,
      false,
      "Priority",
      false,
      false,
      false,
    ],
    footer: "Priority",
  },
  {
    name: "Public Sector and Enterprises",
    description:
      "For departments needing full, secure lifecycle",
    monthlyPrice: "Custom",
    annualPrice: "Custom",
    bgColorCode: "#DEECFF",
    cta: "Talk to Sales",
    features: [
      "Unlimited",
      "Unlimited",
      "Unlimited",
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      "Priority & Named CSM",
      true,
      "Comprehensive",
      "Dedicated",
    ],
    footer: "Priority & Named CSM",
  },
];
